// Capteur de logs embarqué (surtout app Android) — EN PREMIER, avant tout
// autre import, pour attraper les erreurs de boot. Copiable via le bouton 🐞
// (composant DebugConsole). Aucune donnée n'est envoyée : tout reste local.
import { installDebugLogger } from './utils/debugLogger';
// Les sondes de démarrage et les rapports de plantage partent vers l'API
// (api.gively.fr), jamais vers « /api » relatif : sur le web, « /api » tombe sur
// www.gively.fr, qui ne sert pas l'API et répond 404 — relevé dans les logs
// Railway du 30/09/2026 (371 rapports perdus en 9 h, plantages React compris).
import { API_URL } from './config/backend';
installDebugLogger();

// Phase 27H — instrumentation iPhone EN PREMIER (avant tout autre
// import) pour capturer les crashes au boot. Auto-install à l'import.
import './utils/iosDebug';

// 01/10/2026 — GHSA-rvm3-566m-v7fv (Capacitor, Critique) : aucun lien vers le
// proxy interne de Capacitor ne s'ouvre, même dans un binaire non encore mis à
// jour (cf. utils/gardeProxyCapacitor.js).
import { installerGardeProxyCapacitor } from './utils/gardeProxyCapacitor';
installerGardeProxyCapacitor();

// 25/10/2026 — un `detail` 422 (tableau d'objets) passé à toast.error faisait planter
// l'écran (React #31, /register). Filet global : cf. utils/toastTexteSur.js.
import { installerToastTexteSur } from './utils/toastTexteSur';
installerToastTexteSur();

// Paramètres → Affichage → « Réduire les animations » : la dernière valeur
// connue sur cet appareil s'applique dès le boot (la valeur du compte suit).
import { appliquerReduireAnimationsMemorisee, appliquerThemeMemorise } from './utils/affichage';
appliquerReduireAnimationsMemorisee();
appliquerThemeMemorise();

// Phase 8.14 — Monkey-patch Storage UNIQUEMENT sur iOS Safari pour
// éviter les crashes silencieux Private Mode + Storage Restriction
// iOS 18. Sur les autres devices, le storage natif reste intact pour
// que `safeStorage.isStorageAvailable()` continue de probe correctement.
//
// 8+ fichiers utilisent `localStorage.setItem` direct (HomePage,
// SearchPage, NotificationBell etc.) — sans ce patch, QuotaExceededError
// remontait jusqu'à l'ErrorBoundary → fallback "Réparer l'application".
(function _patchStorageOnIOSSafari() {
  if (typeof Storage === "undefined") return;
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  // TOUS les navigateurs iOS sont WebKit (Apple l'impose) : Chrome (CriOS),
  // Firefox (FxiOS), Edge (EdgiOS) ont les MÊMES restrictions de Storage que
  // Safari. L'ancien filtre les excluait → QuotaExceededError non patchée sur
  // Chrome iOS (bug réel 06/07/2026 : éjection de la fiche annonce vers
  // /search quand le cache sessionStorage sature).
  const isWebKitIOS = isIOS; // sur iOS, pas d'exception : tout est WebKit
  if (!isWebKitIOS) return;
  const proto = Storage.prototype;
  const _origGet = proto.getItem;
  const _origSet = proto.setItem;
  const _origRemove = proto.removeItem;
  const _origClear = proto.clear;
  proto.getItem = function (key) {
    try { return _origGet.call(this, key); }
    catch (_) { return null; }
  };
  proto.setItem = function (key, value) {
    try { return _origSet.call(this, key, value); }
    catch (_) { /* ignore — Private mode / quota / restriction */ }
  };
  proto.removeItem = function (key) {
    try { return _origRemove.call(this, key); }
    catch (_) { /* ignore */ }
  };
  proto.clear = function () {
    try { return _origClear.call(this); }
    catch (_) { /* ignore */ }
  };
})();

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { avecSentry, chargerSentry, sentryPret } from './utils/sentryDiffere';
import './index.css';
import App from './App';
import { safeSessionStorage } from './utils/safeStorage';
import { captureAcquisition } from './utils/acquisition';
import { capturerCorpsServeur } from './utils/corpsServeur';
import { modeDiagnosticDemarrage } from './utils/bootDebug';
import { lireConsentement, CLE_CONSENTEMENT } from './utils/consentementCookies';

// Attribution acquisition privacy-first (first-touch UTM / referrer hostname,
// zéro IP, zéro tracker) — cf. docs/ACQUISITION-ATTRIBUTION.md.
try { captureAcquisition(); } catch (_) { /* jamais bloquant */ }

// ─── Phase 27F8.6 — App boot logs structurés (POST telemetry vers
//     backend pour debug iPhone/Safari sans avoir besoin de console).
//     Le user a besoin de voir EXACTEMENT à quel point le boot
//     échoue : module init / chunk load / React mount.
const _bootStart = Date.now();
const _userAgent = (typeof navigator !== "undefined" && navigator.userAgent) || "?";
const _isIOS = /iPhone|iPad|iPod/i.test(_userAgent);
const _isSafari = /Safari/i.test(_userAgent) && !/Chrome|CriOS|FxiOS/i.test(_userAgent);

// 01/10/2026 — les jalons d'un démarrage RÉUSSI ne partent plus vers le
// serveur (ils écrivaient une ligne par visiteur et par jalon) ; APP_CRASH, lui,
// part toujours. Mode diagnostic : `?debug_boot=1` (utils/bootDebug.js).
const _JALONS_SAINS = new Set(["APP_BOOT_START", "APP_BEFORE_REACT_MOUNT", "APP_REACT_MOUNT_SUCCESS"]);
const _diagnosticBoot = modeDiagnosticDemarrage();

function _telemetry(event, extra) {
  if (_JALONS_SAINS.has(event) && !_diagnosticBoot) return;
  try {
    const body = JSON.stringify({
      event,
      build: "phase27f86_boot_audit",
      user_agent: _userAgent.slice(0, 200),
      ios: _isIOS,
      safari: _isSafari,
      elapsed_ms: Date.now() - _bootStart,
      ...(extra || {}),
    });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API_URL}/telemetry/frontend-log`, blob);
    } else if (typeof XMLHttpRequest !== "undefined") {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}/telemetry/frontend-log`, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(body);
    }
  } catch (_) { /* swallow — telemetry non-essentielle */ }
}

// Beacon d'erreur frontend GLOBAL → /api/client-log (corrélé backend via
// request_id). Complète Sentry par une trace Railway côté serveur.
// Garde-fou échelle nationale : on PLAFONNE le nombre d'envois par
// chargement de page (anti-flood / anti-coût) et on borne les tailles.
let _clientErrCount = 0;
const _CLIENT_ERR_CAP = 10;

// Bruit d'EXTENSIONS de navigateur (pas du code Gively) — voir validation
// GIVELY. On filtre par PROVENANCE (schéma d'URL injecté) + signatures de
// messages uniques absentes de notre code. Safari masque les scripts
// d'extension sous `webkit-masked-url://` → indispensable à lister.
const _EXT_NOISE_SCHEMES = /(webkit-masked-url:|chrome-extension:|moz-extension:|safari-extension:|safari-web-extension:|ms-browser-extension:|chrome:\/\/|iabjs:|extensions\/)/i;
const _EXT_NOISE_MSGS = /(browser\.storage|Exceeded storage quota|cashbackReminder|Java object is gone|Error invoking postMessage|navigation_performance_logger)/i;
function _isExtensionNoise(message, stack) {
  try {
    const m = String(message || "");
    const s = String(stack || "");
    if (_EXT_NOISE_MSGS.test(m)) return true;
    // Provenance : la stack vient d'un schéma d'extension/masqué ET ne touche
    // PAS notre bundle (si une frame Gively existe, c'est une vraie erreur).
    if (_EXT_NOISE_SCHEMES.test(s) && !/gively\.fr|\/static\/js\//.test(s)) return true;
    return false;
  } catch (_) { return false; }
}

function reportClientError(payload) {
  try {
    // Drop le bruit d'extensions AVANT de consommer le cap (sinon une vraie
    // erreur de la même page pourrait être évincée par du parasite).
    if (_isExtensionNoise(payload?.message, payload?.stack)) return;
    if (_clientErrCount >= _CLIENT_ERR_CAP) return;
    _clientErrCount += 1;
    const body = JSON.stringify({
      level: "error",
      user_agent: _userAgent.slice(0, 200),
      url: (typeof window !== "undefined" ? String(window.location.href) : "").slice(0, 500),
      ...(payload || {}),
      message: String(payload?.message || "").slice(0, 1000),
      stack: String(payload?.stack || "").slice(0, 2000),
    });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(`${API_URL}/client-log`, new Blob([body], { type: "application/json" }));
    } else if (typeof XMLHttpRequest !== "undefined") {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}/client-log`, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(body);
    }
  } catch (_) { /* never break the app for telemetry */ }
}
// Exposé pour que l'ErrorBoundary React (App.js) puisse remonter ses crashs.
try { window.__giveReportError__ = reportClientError; } catch (_) {}

// Promesses rejetées non gérées (cause fréquente d'états cassés silencieux).
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    _fileSentry(reason instanceof Error ? reason : null);
    reportClientError({
      event: "UNHANDLED_REJECTION",
      message: reason?.message || String(reason || ""),
      stack: reason?.stack || "",
    });
  });
}

// eslint-disable-next-line no-console
console.log("[APP_BOOT_START]", { userAgent: _userAgent, ios: _isIOS, safari: _isSafari });
_telemetry("APP_BOOT_START");

// Sentry error tracking
// Audit du 29/10/2026 (S2) : la mesure de performance (traces) et le Replay
// suivent le consentement « mesure d'audience » du bandeau, comme GA. Avant
// « Accepter », après « Refuser », et toujours dans l'app native : seules les
// erreurs partent (nécessaires au bon fonctionnement du service).
function mesureAcceptee() {
  if (window.__GIVELY_NATIVE__) return false;
  try {
    return lireConsentement(window.localStorage.getItem(CLE_CONSENTEMENT)) === "accepte";
  } catch (_) {
    return false;
  }
}

// Phase 4 — config durcie : release tag + beforeSend filter + ignoreErrors
// (bruit chronique) + denyUrls (3rd-party). Liste basée sur les patterns
// connus en prod et la doc Sentry "Filtering Events" + analyses d'audit.
function initialiserSentry(Sentry) {
  const _mesure = mesureAcceptee();
  try {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      // Initialisé APRÈS l'affichage (voir « Sentry DIFFÉRÉ » plus bas) ; les
      // erreurs du démarrage attendent en file. Le Replay (~50 Ko gzip, rrweb)
      // est chargé ensuite depuis le CDN Sentry (« Lazy Loading Integrations »).
      integrations: _mesure ? [Sentry.browserTracingIntegration()] : [],
      tracesSampleRate: _mesure ? 0.2 : 0,
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0,
      environment: window.location.hostname === 'www.gively.fr' ? 'production' : 'development',
      // Phase 4 — release tag : Railway pose REACT_APP_GIT_COMMIT au build.
      // Permet de grouper les incidents par version dans la dashboard.
      release: process.env.REACT_APP_GIT_COMMIT || "unknown",

      // Phase 4 — bruit chronique à ignorer (bugs connus non-fixables ou
      // gérés ailleurs).
      ignoreErrors: [
        // ChunkLoadError post-deploy : déjà géré par App.js lazyRetry
        // qui force un reload une fois. Pas besoin d'alerter Sentry.
        "ChunkLoadError",
        "Loading chunk",
        "Loading CSS chunk",
        // Bug WebKit / Chrome bénin sur ResizeObserver — reporté dans
        // les specs comme "non-actionable" :
        //   https://github.com/WICG/resize-observer/issues/38
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Erreurs de plugins navigateur / scripts injectés (ad-block,
        // password manager, traducteur) — pas de notre code.
        "Script error.",
        "Non-Error promise rejection captured",
        // Extensions WebExtension (gestionnaire de stockage) + cashback
        // (Honey/Rakuten…) : ces tokens n'existent NULLE PART dans le code
        // Gively (grep confirmé) → filtrage par signature de message sûr.
        /browser\.storage/,
        /Exceeded storage quota/,
        /cashbackReminder/,
        // PayPal SDK lance ces messages quand l'utilisateur ferme la
        // popup ou bloque les cookies — pas un bug applicatif.
        /window\.paypal\.Buttons.*not\s+a\s+function/,
        /Detected popup close/,
        // Navigateur in-app Facebook/Instagram/Android WebView : injecte son
        // propre JS (iabjs://navigation_performance_logger_android) et lance
        // ce postMessage quand le pont Java de la WebView est détruit à la
        // navigation. Code 100 % tiers (browser=Facebook), non corrigeable,
        // arrive massivement sur les liens partagés avec ?fbclid=.
        "Java object is gone",
        "Error invoking postMessage",
        /navigation_performance_logger/,
      ],

      // Phase 4 — origines à ignorer complètement (3rd-party qui
      // injectent du JS dans la page sans nous demander).
      denyUrls: [
        // Extensions navigateur Chrome / Firefox / Safari.
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        /^safari-web-extension:\/\//,
        // Safari masque les scripts d'extension injectés sous ce schéma
        // (origine des erreurs "cashbackReminder" / "browser.storage").
        /^webkit-masked-url:\/\//,
        /^safari-extension:\/\//,
        /^ms-browser-extension:\/\//,
        /^chrome:\/\//,
        // Service workers tiers / cdn d'analytics qui balancent
        // parfois des erreurs en notre nom.
        /googletagmanager\.com/,
        /google-analytics\.com/,
        // JS injecté par les navigateurs in-app (Facebook/Instagram/WebView
        // Android) — schéma iabjs://. Pas notre code.
        /^iabjs:\/\//,
      ],

      // Phase 4 — filtre fin appliqué APRÈS ignoreErrors/denyUrls.
      // Permet de filtrer sur des conditions plus complexes (URL +
      // status code combinés, etc.).
      beforeSend(event, hint) {
        try {
          // Filet le plus fiable (validation GIVELY) : drop si l'event vient
          // d'une extension/URL masquée et ne touche AUCUNE frame Gively.
          // Rattrape ce que denyUrls rate (stacks masquées webkit-masked-url).
          const _msg = String(hint?.originalException?.message || event?.message || "");
          if (_EXT_NOISE_MSGS.test(_msg)) return null;
          const _frames = [];
          (event?.exception?.values || []).forEach((v) => {
            (v?.stacktrace?.frames || []).forEach((f) => {
              if (f?.filename) _frames.push(String(f.filename));
            });
          });
          const _blob = _frames.join(" ") + " " + String(event?.culprit || event?.transaction || "");
          const _hasOurFrame = _frames.some((f) => /gively\.fr|\/static\/js\//.test(f));
          if (!_hasOurFrame && _EXT_NOISE_SCHEMES.test(_blob)) return null;

          const exc = hint?.originalException;
          // Filtre les erreurs réseau axios sur les endpoints publics
          // qui ont leur propre handling (status 4xx attendus).
          const status = exc?.response?.status;
          const url = String(exc?.config?.url || event?.request?.url || "");
          // 401/403 sont attendus quand le user n'est pas connecté.
          if (status === 401 || status === 403) return null;
          // 409 sur create-order/quote = divergence montant attendue.
          // (ancien chemin paypal/* des vieux bundles + alias neutre actuel)
          if (status === 409 && /(paypal\/(create-order|quote)|purchase\/quote)/.test(url)) {
            return null;
          }
          // 429 rate-limit côté backend, pas un bug applicatif.
          if (status === 429) return null;
        } catch (_) { /* never break Sentry */ }
        return event;
      },
    });
  } catch (_e) {
    // Sentry init can throw on storage-restricted browsers (iOS Private).
    // We swallow — telemetry is non-essential for rendering the app.
  }

  // REPLAY en LAZY-LOAD (best practice officielle Sentry, méthode npm) : on
  // l'ajoute APRÈS le 1er paint via import() dynamique → le bundler sort rrweb
  // (~50 Ko gzip) du bundle initial dans un chunk séparé. Le core ci-dessus
  // capture déjà toutes les erreurs du boot, donc on ne perd rien.
  // Désactivé sur iOS Safari (MutationObserver/IndexedDB throwent en Private).
  if (_mesure && !(_isIOS && _isSafari)) {
    const _loadReplay = () => {
      // 25/10/2026 : `import("@sentry/react")` visait un module DÉJÀ dans le bundle
      // principal → rrweb y restait (≈ 0,5 Mo de source au démarrage, écran de
      // secours sur réseau lent). lazyLoadIntegration charge Replay depuis le CDN
      // Sentry, hors bundle (doc Sentry « Lazy Loading Integrations »).
      Sentry.lazyLoadIntegration("replayIntegration")
        .then((replayIntegration) => {
          Sentry.addIntegration(replayIntegration({ maskAllText: true, blockAllMedia: false }));
        })
        .catch(() => { /* ad-blocker/réseau : rien perdu, le core tourne déjà */ });
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(_loadReplay, { timeout: 3000 });
    } else {
      setTimeout(_loadReplay, 2000);
    }
  }
}


// Sentry DIFFÉRÉ (27/10/2026) : le SDK (≈ 121 Ko non compressés, 17 % de main.js)
// n'est plus dans le fichier de démarrage. Chargé une fois la page affichée et le
// navigateur au repos ; les erreurs survenues avant sont mises en file par
// utils/sentryDiffere et rejouées au chargement (rien n'est perdu), et le
// signalement serveur /client-log les trace de toute façon.
if (process.env.REACT_APP_SENTRY_DSN && typeof window !== "undefined") {
  const _charger = () => { chargerSentry(initialiserSentry); };
  const _auRepos = () => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(_charger, { timeout: 3000 });
    else setTimeout(_charger, 1500);
  };
  if (document.readyState === "complete") _auRepos();
  else window.addEventListener("load", _auRepos, { once: true });
}

// Erreur levée AVANT le chargement de Sentry : mise en file pour lui. Après le
// chargement, ses propres écouteurs globaux prennent le relais (pas de doublon).
function _fileSentry(erreur) {
  if (!process.env.REACT_APP_SENTRY_DSN || sentryPret() || !erreur) return;
  avecSentry((S) => S.captureException(erreur));
}

// Auto-reload on stale chunks after deployment (ChunkLoadError / SyntaxError).
//
// Phase 8.14 — fix boucle reload infinie iOS Safari Private :
// avant, le flag `gively_chunk_reload` etait stocke via
// safeSessionStorage qui retourne null silencieusement si bloque ->
// le `if (!getItem(reloadKey))` etait TOUJOURS vrai -> reload infini.
// Maintenant : double-stockage sessionStorage + URL hash. Le hash
// survit au reload meme si sessionStorage est bloque par iOS Private.
window.addEventListener("error", (event) => {
  const msg = event.message || "";
  const isChunkError =
    msg.includes("ChunkLoadError") ||
    msg.includes("Loading chunk") ||
    msg.includes("Unexpected token '<'") ||
    msg.includes("Loading CSS chunk");
  // Erreur JS réelle (hors stale-chunk, géré par reload) → beacon serveur.
  if (!isChunkError && (event.error || msg)) {
    _fileSentry(event.error);
    reportClientError({
      event: "WINDOW_ERROR",
      message: msg || event.error?.message || "",
      stack: event.error?.stack || "",
      source: event.filename ? `${event.filename}:${event.lineno || 0}:${event.colno || 0}` : "",
    });
  }
  if (isChunkError) {
    console.warn("[Gively] Stale chunk detected, reloading...");
    const reloadKey = "gively_chunk_reload";
    const HASH_FLAG = "#chunk-reloaded";
    const alreadyReloaded =
      safeSessionStorage.getItem(reloadKey) === "1" ||
      (window.location.hash || "").indexOf("chunk-reloaded") !== -1;
    if (!alreadyReloaded) {
      safeSessionStorage.setItem(reloadKey, "1");
      // Backup hash si sessionStorage bloque (iOS Private).
      try {
        const url = new URL(window.location.href);
        url.hash = HASH_FLAG;
        window.location.replace(url.toString());
      } catch (_) {
        window.location.reload();
      }
    } else {
      // 2eme erreur de chunk apres reload -> on ne reload PAS, on
      // laisse l'utilisateur voir le fallback "Reparer l'application".
      console.error("[Gively] Chunk error after reload — abandon reload loop.");
    }
  }
});
// Clear reload flag on successful load (sessionStorage + hash).
safeSessionStorage.removeItem("gively_chunk_reload");
try {
  if ((window.location.hash || "").indexOf("chunk-reloaded") !== -1) {
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState(null, "", url.toString());
  }
} catch (_) { /* ignore */ }

// Phase 9.7 — au mount React reussi, nettoyer le hash #ios-killed
// au cas ou un ancien build l'aurait laisse trainer (les nouveaux
// builds utilisent window.name au lieu du hash). Sans ce cleanup,
// le hash reste dans la barre d'URL meme apres React mount, ce qui
// est cosmetiquement moche.
try {
  if ((window.location.hash || "").indexOf("ios-killed") !== -1) {
    const url2 = new URL(window.location.href);
    url2.hash = "";
    window.history.replaceState(null, "", url2.toString());
  }
} catch (_) { /* ignore */ }

// Listen for service worker "stale chunk" signal
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "RELOAD_NEEDED" && !safeSessionStorage.getItem("gively_chunk_reload")) {
      console.warn("[Gively] SW detected stale chunk, reloading...");
      safeSessionStorage.setItem("gively_chunk_reload", "1");
      window.location.reload();
    }
  });
}

// ─── Enregistrement du Service Worker — Android / desktop UNIQUEMENT ───
// Le SW (public/sw.js) n'était JAMAIS enregistré → PWABuilder le voyait
// absent et refusait de générer le bundle Android (TWA). On l'enregistre
// donc ici, MAIS jamais sur iOS Safari/WebKit : la saga « écran gris
// iPhone » (bugs WebKit 199928/215884) a imposé un kill-switch et un SW
// y est nocif. Le sw.js a aussi sa propre double protection iOS
// (auto-unregister + bypass fetch), mais on verrouille dès le register.
//
// PIÈGE iPadOS (≥13) : l'iPad envoie un UA « Macintosh » SANS « iPad » →
// on le rattrape via navigator.maxTouchPoints (un vrai Mac = 0 touch).
function _isAppleWebKit() {
  const ua = navigator.userAgent || "";
  const isClassicIOS = /iPhone|iPad|iPod/i.test(ua);
  const isIPadOS =
    /Macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  const isIOS = isClassicIOS || isIPadOS;
  // Sur iOS, TOUS les navigateurs sont WebKit (CriOS/FxiOS inclus) → même
  // bug → on les exclut tous du register.
  const isWebKitBrowser = /Safari/i.test(ua) || /CriOS|FxiOS/i.test(ua);
  return isIOS && isWebKitBrowser;
}

// Ne JAMAIS enregistrer le service worker dans l'app native (Capacitor) : le
// bundle est déjà local et immuable jusqu'au prochain AAB ; un SW cache-first
// ajoute une couche de cache NON purgeable côté utilisateur qui peut servir un
// vieux app-shell/chunk → écran figé après mise à jour. (Audit Android 2026-07,
// reco Capacitor.) `window.Capacitor` n'existe que dans la WebView native.
if ("serviceWorker" in navigator && !_isAppleWebKit() && !window.Capacitor) {
  // Enregistrement IMMÉDIAT (pas d'attente de window.load) : PWABuilder et
  // les crawlers PWA testent vite ; si on attend `load`, ils peuvent passer
  // avant que le SW soit enregistré et le rapporter « absent ». Le coût
  // (concurrence avec les chunks) est négligeable, la requête /sw.js est
  // minuscule et le SW ne pré-cache qu'en best-effort.
  navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((reg) => {
      console.log("[SW_REGISTERED]", reg.scope);
      // En TWA Android, l'app reste ouverte des heures : sans re-vérification,
      // le navigateur peut tarder à détecter un nouveau /sw.js (donc une nouvelle
      // version). On force un update() au retour au premier plan + toutes les
      // 30 min. Combiné au tampon BUILD_ID au build (sw.js change à chaque deploy),
      // ça garantit la bascule vers la version fraîche sans intervention.
      // INCIDENT Sentry 18/08/2026 (aec85885, Chrome 151/Windows, /publish) :
      // « InvalidStateError: Failed to update a ServiceWorker … The object is
      // in an invalid state ». Deux bugs en un :
      //   1. `reg.update()` renvoie une PROMESSE — le try/catch SYNCHRONE ne
      //      pouvait RIEN attraper : le rejet partait en `unhandledrejection`
      //      (Sentry : handled=no, mechanism=onunhandledrejection).
      //   2. Conséquence UTILISATEUR, la vraie : `update()` en échec = plus
      //      aucune détection de nouvelle version pendant la session → le
      //      navigateur continue de servir l'ANCIEN bundle après un déploiement
      //      (« c'est toujours pareil » alors que le correctif est en ligne).
      // Correctif : rejet capturé, et registration RÉPARÉE (re-register) quand
      // elle est devenue invalide — un `unregister()` concurrent (bouton « vider
      // le cache » du debug), un SW passé `redundant` ou un onglet resté ouvert
      // des heures y mènent. Garde anti-concurrence : jamais deux updates en vol.
      let _updating = false;
      let _reg = reg;
      const _upd = () => {
        if (_updating || !_reg) return;
        _updating = true;
        Promise.resolve()
          .then(() => _reg.update())
          .catch((e) => {
            const name = String((e && e.name) || "");
            console.warn("[SW_UPDATE_FAILED]", name, e);
            if (name === "InvalidStateError" || name === "InvalidAccessError") {
              // Registration inutilisable : on repart d'une registration saine,
              // sinon l'utilisateur reste bloqué sur l'ancienne version.
              return navigator.serviceWorker
                .register("/sw.js", { scope: "/" })
                .then((fresh) => { _reg = fresh; console.log("[SW_REREGISTERED]", fresh.scope); })
                .catch((e2) => console.warn("[SW_REREGISTER_FAILED]", e2));
            }
            return undefined;
          })
          .finally(() => { _updating = false; });
      };
      setInterval(_upd, 30 * 60 * 1000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") _upd();
      });
    })
    .catch((e) => console.warn("[SW_REGISTER_FAILED]", e));
}

// Phase 27F8.6 — boot React avec catch global pour ne JAMAIS laisser
// l'utilisateur sur l'écran "Chargement..." silencieusement.
// eslint-disable-next-line no-console
console.log("[APP_BEFORE_REACT_MOUNT]");
_telemetry("APP_BEFORE_REACT_MOUNT");
// Phase 9.7 — telemetrie ios_debug dediee (persiste en BDD).
try { window.__giveLog__ && window.__giveLog__("react_mount_attempt"); } catch (_) {}

// Le corps servi par le serveur (titre, texte, liens) est lu AVANT que React ne
// remplace #root : une page dont l'API échoue peut encore l'afficher (11/10/2026).
capturerCorpsServeur();

try {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(<React.StrictMode><HelmetProvider><App /></HelmetProvider></React.StrictMode>);
  // eslint-disable-next-line no-console
  console.log("[APP_REACT_MOUNT_SUCCESS]");
  _telemetry("APP_REACT_MOUNT_SUCCESS");
  try { window.__giveLog__ && window.__giveLog__("react_mount_success"); } catch (_) {}
} catch (err) {
  // eslint-disable-next-line no-console
  console.error("[APP_CRASH]", err);
  _telemetry("APP_CRASH", {
    error: String(err?.message || err).slice(0, 480),
    stack: String(err?.stack || "").slice(0, 1500),
  });
  try {
    window.__giveLog__ && window.__giveLog__("react_mount_error", {
      error_message: String(err?.message || err),
      error_stack: String(err?.stack || ""),
    });
  } catch (_) {}
  // Fallback : afficher un bouton de récupération minimal.
  try {
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.innerHTML = (
        '<div style="font-family:-apple-system,BlinkMacSystemFont,sans-serif;'
        + 'padding:48px 20px;max-width:480px;margin:0 auto;text-align:center">'
        + '<h1 style="color:#1DA1F2;font-size:22px;margin:0 0 12px">Gively</h1>'
        + '<p style="color:#475569;font-size:14px;margin:0 0 16px">'
        + 'Une erreur est survenue au chargement. Cliquez sur le bouton '
        + 'ci-dessous pour réparer l\'application.</p>'
        + '<button onclick="(async()=>{try{if(\'caches\' in window){const ks=await caches.keys();await Promise.all(ks.map(k=>caches.delete(k)));}if(\'serviceWorker\' in navigator){const rs=await navigator.serviceWorker.getRegistrations();await Promise.all(rs.map(r=>r.unregister()));}}catch(e){}finally{window.location.reload(true);}})()" '
        + 'style="background:#1DA1F2;color:white;border:0;border-radius:10px;'
        + 'padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer">'
        + 'Réparer l\'application</button></div>'
      );
    }
  } catch (_) { /* DOM unavailable */ }
}
